import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { OrgChartNode } from '@/types/hr'
import { countryFlag } from './countryFlags'

function getInitials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

function OrgNodeView({
  node,
  depth,
  expandSignal,
}: {
  node: OrgChartNode
  depth: number
  expandSignal: boolean
}) {
  const [expanded, setExpanded] = useState(true)
  // Follow the parent "Expand/Collapse all" toggle, while still allowing
  // per-branch toggling in between (no remount → screen-reader position kept).
  useEffect(() => {
    setExpanded(expandSignal)
  }, [expandSignal])
  const hasChildren = node.children && node.children.length > 0
  const name = `${node.preferred_name || node.first_name} ${node.last_name}`
  const flag = countryFlag(node.country)

  return (
    <li role="treeitem" aria-expanded={hasChildren ? expanded : undefined}>
      <div className="flex items-center gap-2 py-1">
        {hasChildren ? (
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? `Collapse ${name}'s reports` : `Expand ${name}'s reports`}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        ) : (
          <span className="inline-block h-6 w-6" aria-hidden="true" />
        )}
        <Link
          to={`/hr/employees/${node.id}`}
          className="flex flex-1 items-center gap-3 rounded px-2 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {node.profile_photo_url ? (
            <img
              src={node.profile_photo_url}
              alt=""
              className="h-8 w-8 rounded-full bg-muted object-cover"
            />
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
              aria-hidden="true"
            >
              {getInitials(node.first_name, node.last_name)}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p className="truncate font-medium">{name}</p>
              {flag && (
                <span role="img" aria-label={flag.label} title={flag.label} className="shrink-0">
                  {flag.emoji}
                </span>
              )}
            </div>
            {(node.job_title || node.department_name) && (
              <p className="truncate text-xs text-muted-foreground">
                {[node.job_title, node.department_name].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </Link>
      </div>
      {hasChildren && expanded && (
        <ul
          role="group"
          className="border-l border-border pl-4"
          style={{ marginLeft: `${Math.min(8, 4 + depth * 2) * 0.25}rem` }}
        >
          {node.children.map((child) => (
            <OrgNodeView
              key={child.id}
              node={child}
              depth={depth + 1}
              expandSignal={expandSignal}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

/** Accessible, keyboard/screen-reader-friendly tree view — the WCAG-conformant
 * alternative to the visual diagram. */
export function OrgChartListView({ roots }: { roots: OrgChartNode[] }) {
  const [allExpanded, setAllExpanded] = useState(true)
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-sm font-medium">Reporting Hierarchy</CardTitle>
          <CardDescription>
            Use Tab to navigate, Enter to open a profile, and the chevron to expand or collapse a
            branch.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAllExpanded((e) => !e)}>
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </CardHeader>
      <CardContent>
        {roots.length === 0 ? (
          <p className="text-muted-foreground">
            No employees yet. Add an employee or import a CSV to start building your org chart.
          </p>
        ) : (
          <ul role="tree" aria-label="Organizational hierarchy">
            {roots.map((root) => (
              <OrgNodeView key={root.id} node={root} depth={0} expandSignal={allExpanded} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
